import type { ProfileDisplaySummary } from './profileDisplaySummary';
import {
  isLiteRegisterPlaceholderBirthDate,
  isLiteRegisterPlaceholderFirstName,
  isLiteRegisterPlaceholderLastName,
  isLiteRegisterPlaceholderPhoneFirstName,
  isLiteRegisterPlaceholderPhoneNumber,
  isPhoneLiteRegisterSyntheticEmail,
} from './profileLitePlaceholders';

export const PRIVATE_CLIENT_USER_INFO_KEYS = [
  'first_name',
  'last_name',
  'email',
  'phone_number',
  'birth_date',
] as const;

export type PrivateClientUserInfoKey = (typeof PRIVATE_CLIENT_USER_INFO_KEYS)[number];

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function emailLooksValid(email: string): boolean {
  return email.includes('@') && !email.startsWith('@') && !email.endsWith('@');
}

/** True when a stored registration value is empty, a lite placeholder, or otherwise unusable. */
export function privateClientUserInfoValueIsProblem(
  key: PrivateClientUserInfoKey,
  snapshot: Record<string, unknown> | null | undefined,
): boolean {
  const first = pickString(snapshot?.first_name) || pickString(snapshot?.firstName);
  const last = pickString(snapshot?.last_name) || pickString(snapshot?.lastName);
  const email =
    pickString(snapshot?.email) || pickString(snapshot?.Email) || pickString(snapshot?.loginId);
  const phone =
    pickString(snapshot?.phone_number) ||
    pickString(snapshot?.phone) ||
    pickString(snapshot?.phoneNumber);
  const birthDate =
    pickString(snapshot?.birth_date) ||
    pickString(snapshot?.date_of_birth) ||
    pickString(snapshot?.dateOfBirth);

  switch (key) {
    case 'first_name':
      return (
        !first ||
        isLiteRegisterPlaceholderPhoneFirstName(first, email) ||
        isLiteRegisterPlaceholderFirstName(first, email, { lastName: last })
      );
    case 'last_name':
      return !last || isLiteRegisterPlaceholderLastName(last);
    case 'email':
      return !email || !emailLooksValid(email) || isPhoneLiteRegisterSyntheticEmail(email);
    case 'phone_number':
      return !phone || isLiteRegisterPlaceholderPhoneNumber(phone);
    case 'birth_date':
      return !birthDate || isLiteRegisterPlaceholderBirthDate(birthDate);
    default:
      return false;
  }
}

export function incompletePrivateClientUserInfoKeys(
  snapshot: Record<string, unknown> | null | undefined,
): PrivateClientUserInfoKey[] {
  return PRIVATE_CLIENT_USER_INFO_KEYS.filter((key) =>
    privateClientUserInfoValueIsProblem(key, snapshot),
  );
}

export function incompletePrivateClientUserInfoKeysFromSummary(
  summary: ProfileDisplaySummary | null | undefined,
): PrivateClientUserInfoKey[] {
  if (!summary) {
    return [...PRIVATE_CLIENT_USER_INFO_KEYS];
  }
  return incompletePrivateClientUserInfoKeys({
    first_name: summary.firstName,
    last_name: summary.lastName,
    email: summary.email,
    phone_number: summary.phone,
    birth_date: summary.dateOfBirth,
  });
}